import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { applyReferralCode, sendWelcomeEmail, updateFirstName } from '../api/atlast';
import { theme } from '../theme';
import { PasswordVisibilityToggle } from '../Icons';
import { checkPasswordStrength, PASSWORD_REQUIREMENTS_MESSAGE, PasswordStrengthMeter } from '../PasswordStrength';
import { Preferences } from '@capacitor/preferences';
import AppFooter, { PinLogo } from '../AppFooter';

// Rewritten Aug 22 (per Ali's report) to match the Wayfinding navy/
// yellow system the rest of the app moved to on Aug 18 — this page was
// the one screen never migrated, still hardcoding the old warm-cream/
// orange-gradient look directly rather than reading theme tokens, which
// is why it visually stood out as "an old concept" next to Dashboard/
// DealScreen/FreezeDetailScreen. The four fake mockup "preview" slides
// that used to fill the brand-panel carousel (a typed-out fake deal
// card, a fake dashboard, a fake booking click) are gone entirely —
// replaced with real, cropped screenshots of actual confirmed deals
// (Edinburgh, Delhi, Toronto), since a real product screenshot is a
// stronger, more honest proof point than a mockup pretending to be one.
// The Stories-style rotating carousel mechanism itself (progress bars,
// click-to-jump, staggered title entrance) is kept as-is — it was
// already solid, tested UX; only its content changed.

// Three plain steps shown beside the form. Same wording as the website.
const BRAND_POINTS = [
  { t: 'Enter your trip and budget', d: 'Your route, your dates and the most you want to pay.' },
  { t: 'We check real fares', d: 'More often as your departure gets closer.' },
  { t: 'You get an email when a fare fits', d: 'It links to the full itinerary in the app.' },
];

function LoginHeader() {
  return (
    <header className="login-top">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <PinLogo />
        <span style={{ fontFamily: theme.fontDisplay, fontSize: '22px', fontWeight: 800, color: '#1E3A5F', letterSpacing: '-0.3px' }}>FlyAtlast</span>
      </div>
      <a href="https://flyatlast.com" style={{ fontSize: '14px', fontWeight: 700, color: '#1E3A5F', textDecoration: 'none' }}>About FlyAtlast &rarr;</a>
    </header>
  );
}

function Login() {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  // New (Aug 31, per Ali's report) — a real, self-service "Forgot
  // password?" flow. Before this, resetting a password was only
  // possible via Ali manually triggering it from Supabase's admin
  // dashboard on someone's behalf — a real gap, fine for tonight's
  // handful of test accounts but not workable for actual users at any
  // scale. A separate flag rather than folding into isSignup — this
  // page already branches heavily on isSignup throughout (headline,
  // button text, submit handler), and forgot-password is a genuinely
  // different form (email only, no password field) shown instead of
  // that whole form, not a third value the existing isSignup ternaries
  // would need to be rewritten to handle.
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // Password visibility toggle (Sep 7, per external review feedback —
  // real, reasonable complaint: no way to check what you typed before
  // submitting, so a typo just fails silently as a wrong-password error
  // with no way to tell "wrong password" from "fat-fingered it").
  const [showPassword, setShowPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '' });
  // Referral-aware landing (Aug 22, per Ali's report) — every referral
  // link points straight at this page, but it used to default to login
  // mode regardless, meaning a brand-new visitor clicking a friend's
  // invite landed on "Welcome back" with Sign In as the dominant action
  // and Sign Up demoted to a small text link — backwards for someone
  // who has never had an account. Detecting the code and defaulting to
  // signup mode (plus actually telling them why) fixes the one page
  // sitting at the front of the app's main growth channel.
  const [referralCode, setReferralCode] = useState(null);

  // Captured once on mount, not read fresh on every render — a referral
  // link's code should survive the user switching from the signup form
  // to the login form (this app requires a separate manual login after
  // signup, it doesn't auto-session), so it's persisted via Capacitor
  // Preferences (swapped from localStorage on Aug 23, per Ali's report,
  // for the Capacitor mobile build — plain browser localStorage isn't
  // guaranteed to persist reliably inside a native WebView) rather than
  // kept only in URL/component state, which the intervening "Account
  // created! You can now log in" step would lose.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      const code = ref.trim().toUpperCase();
      Preferences.set({ key: 'flyatlast_pending_referral', value: code });
      setReferralCode(code);
      setIsSignup(true);
    } else if (params.get('signup') === 'true') {
      // General marketing-site CTAs (Aug 23, per Ali's report) — same
      // fix as the referral case above, generalized. Every CTA on
      // flyatlast.com sends a brand-new visitor here with this param
      // now, specifically so they land on "Create account" instead of
      // "Welcome back" — the same wrong-default-mode bug the ?ref= case
      // already fixed, just previously left unfixed for the far more
      // common case of someone arriving from the marketing site rather
      // than a referral link.
      setIsSignup(true);
    }
  }, []);


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isSignup) {
        const strength = checkPasswordStrength(form.password);
        if (!strength.valid) {
          alert(PASSWORD_REQUIREMENTS_MESSAGE);
          setLoading(false);
          return;
        }

        // Pass the browser's detected timezone as signup metadata — the
        // handle_new_user() trigger reads this to populate users.timezone,
        // which active-hours notification gating depends on. If this
        // fails to detect (rare), timezone just stays null and gating
        // falls back to "always active" until it's captured some other way.
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const { data, error } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
          options: {
            data: { timezone }
          }
        });
        if (error) throw error;
        // Diagnostic logging added (Sep 9, per Ali's report — a real
        // signup produced no welcome email, and none of the three
        // usual checks — spam, backend logs, provider send log — found
        // any trace of an attempt at all. That absence of evidence is
        // itself a signal: it points at this call never firing in the
        // first place, not at it firing and failing silently further
        // downstream. This makes both possibilities visible in the
        // browser console going forward, cutting through the guessing
        // rather than adding a fourth blind diagnostic step.
        if (!data?.user?.id) {
          console.warn('[signup] supabase.auth.signUp() succeeded but returned no data.user.id — welcome email cannot fire. Full data:', data);
        }
        // New (Aug 31, per Ali's report — Phase C, item #4): fire-and-
        // forget, same "call a backend action right after signUp
        // succeeds" pattern already used below for applying a pending
        // referral code on login — not awaited/error-handled here on
        // purpose, since a failed welcome email should never block or
        // surface an error during signup itself. data.user is only
        // reliably present here (right after signUp) rather than in
        // the login branch below, since that's the one moment this
        // code knows for certain "this is a brand-new account."
        if (data?.user?.id) {
          sendWelcomeEmail(data.user.id)
            .then((result) => {
              if (result?.status !== 'sent') {
                // The backend already explains itself clearly here
                // (e.g. "no email on file", or "resend_api_key not
                // set") — the old code just never looked, and its
                // outer wrapper used to always say "ok" regardless of
                // what actually happened, so even reading it wouldn't
                // have helped until that was fixed too (see
                // routes/users.py). Logged, not surfaced to the user,
                // matching the existing "never alarm someone over a
                // welcome email" principle — this is for debugging.
                console.warn('[signup] welcome email did not send:', result);
              }
            })
            .catch((err) => console.warn('[signup] welcome email request failed:', err));
          // First name (Sep 8, per Ali's report) — optional, so only
          // fired if actually provided. Same fire-and-forget shape as
          // the welcome email above, and the same accepted risk noted
          // on the backend endpoint itself: if no session exists yet
          // at this exact moment (Supabase's email-confirmation
          // timing), this silently fails and first_name just stays
          // null — recoverable later via the editable "Change name"
          // screen, not worth blocking or surfacing an error over.
          if (form.first_name.trim()) {
            updateFirstName(data.user.id, form.first_name.trim()).catch(() => null);
          }
        }
        alert('Account created! You can now log in.');
        setIsSignup(false);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password
        });
        if (error) throw error;

        // Block sign-in for a soft-deleted account (Sep 7, per Ali's
        // report — Phase 2 of account settings). Deliberately checked
        // here, right at the one real place a normal user actually
        // authenticates, rather than inside get_current_user_id itself
        // — that function is a pure JWT-verification check with no
        // database call at all, shared by every single route in the
        // backend; adding a deleted_at lookup there would add a DB
        // query to every authenticated request app-wide, which is a
        // much bigger architectural change than this feature called
        // for. A soft-deleted account's session token could in theory
        // still reach a backend route directly (bypassing this screen
        // entirely), but that requires deliberately hand-crafting API
        // calls rather than anything a real user's flow would do — an
        // accepted, low-risk gap, not an oversight.
        const { data: userRow } = await supabase
          .from('users')
          .select('deleted_at')
          .eq('id', data.user.id)
          .single();
        if (userRow?.deleted_at) {
          await supabase.auth.signOut();
          // Improved message (Sep 9, per Ali's report — a real signup/
          // login loop that confused him on his own test account: the
          // old message didn't explain that re-signing up with the
          // same email won't work either during the 30-day window, or
          // give any concrete deadline for support to act by). Computed
          // from the real deleted_at value already fetched above, not
          // a fixed "30 days" restated as text that could drift out of
          // sync with the backend's own actual window.
          const deletedDate = new Date(userRow.deleted_at);
          const restoreDeadline = new Date(deletedDate.getTime() + 30 * 24 * 60 * 60 * 1000);
          const deadlineStr = restoreDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          throw new Error(
            `This account was deleted on ${deletedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. ` +
            `It can still be restored — contact contactus@flyatlast.com before ${deadlineStr} to get it back. ` +
            `Signing up again with this email won't work until then either. After ${deadlineStr}, the account is permanently removed and this email can be used again.`
          );
        }

        // Apply any pending referral code exactly once, right after a
        // successful login — cleared from Preferences regardless of
        // the outcome (applied, already-applied, invalid, self-referral)
        // so a stale/bad code doesn't keep retrying on every future login.
        const stored = await Preferences.get({ key: 'flyatlast_pending_referral' });
        const pendingReferral = stored.value;
        if (pendingReferral && data?.user?.id) {
          await applyReferralCode(data.user.id, pendingReferral);
          await Preferences.remove({ key: 'flyatlast_pending_referral' });
        }

        navigate('/dashboard');
      }
    } catch (error) {
      alert(error.message);
    }
    setLoading(false);
  };

  // New (Aug 31, per Ali's report) — the actual self-service reset
  // trigger. resetPasswordForEmail is a public, unauthenticated Supabase
  // Auth method — no admin/service-role access needed, unlike the
  // Authentication -> Users dashboard action Ali's been using manually
  // all night. Explicitly passes redirectTo rather than relying solely
  // on the project's Site URL setting — defense in depth against
  // exactly the kind of misconfiguration that caused tonight's whole
  // "recovery links point at a dead localhost address" problem in the
  // first place; even if Site URL ever drifts again, this call still
  // lands correctly on this app's own /reset-password route.
  const handleForgotPassword = async () => {
    setForgotError(null);
    if (!forgotEmail) {
      setForgotError('Enter your email first.');
      return;
    }
    setForgotSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
    } catch (error) {
      // Deliberately the SAME message regardless of whether the email
      // actually exists in the system — same reasoning Supabase's own
      // "Invalid login credentials" uses on the login form: a distinct
      // "no account with that email" error would let someone probe
      // which emails have accounts here at all.
      setForgotError('If an account exists for that email, a reset link is on its way.');
    }
    setForgotSubmitting(false);
  };

  return (
    <div className="login-shell">
    <LoginHeader />
    <div className="login-page">
      {/* Compact brand header — mobile only (App.css hides this at 900px
          and switches to the full .login-brand-panel instead). */}
      <div className="login-mobile-brand">
        <div className="login-mobile-brand-tagline">
          Set your budget and we’ll watch the fares.
        </div>
      </div>

      {/* Brand panel — hidden on narrow screens via App.css media query,
          so mobile keeps the compact single-column form below. Kept
          undecorated (Aug 22, per Ali's report) — an earlier pair of
          faint square outlines here was tested live and read as a
          rendering glitch rather than intentional decoration, so it was
          removed rather than tuned. The logo, headline, and real deal
          carousel below carry the panel on their own. */}
      <div className="login-brand-panel">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', width: '100%' }}>
          <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '660px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <h1 style={{ fontSize: '48px', fontWeight: 800, color: '#fff', margin: '0 0 12px', fontFamily: theme.fontDisplay, lineHeight: 1.06, letterSpacing: '-0.5px' }}>
              Set your budget and we’ll watch the fares.
            </h1>
            <p style={{ fontSize: '17px', color: '#C9D6E3', lineHeight: 1.5, margin: '0 0 30px', maxWidth: '560px' }}>
              Tell us where you want to go and the most you want to pay. We check the fares for you and email you when one fits.
            </p>

            <ol style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', maxWidth: '520px' }}>
              {BRAND_POINTS.map((s, n) => (
                <li key={s.t} style={{ position: 'relative', paddingLeft: '42px', marginBottom: '20px' }}>
                  <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, width: '28px', height: '28px', border: '1px solid #7F9AC0', borderRadius: '50%', textAlign: 'center', lineHeight: '26px', fontSize: '14px', color: '#fff' }}>{n + 1}</span>
                  <div style={{ fontWeight: 700, color: '#fff', fontSize: '17px' }}>{s.t}</div>
                  <div style={{ color: '#C9D6E3', fontSize: '15px' }}>{s.d}</div>
                </li>
              ))}
            </ol>
            <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.7)', marginTop: '10px' }}>Examples only. Prices are illustrative.</div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="login-form-panel">
        <div className="login-card">
          <div className="login-card-body">
            {!showForgotPassword && (
              <div className="login-seg" role="tablist">
                <button type="button" role="tab" aria-selected={isSignup} className={isSignup ? 'on' : ''} onClick={() => setIsSignup(true)}>Create account</button>
                <button type="button" role="tab" aria-selected={!isSignup} className={!isSignup ? 'on' : ''} onClick={() => setIsSignup(false)}>Sign in</button>
              </div>
            )}

            {showForgotPassword ? (
              // New (Aug 31, per Ali's report) — real self-service
              // password reset, replacing the whole email+password form
              // rather than sitting alongside it, since this is a
              // genuinely different action (send a link, don't try to
              // sign in right now) not a variant of the same form.
              <>
                <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
                  Reset your password
                </h2>
                <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 24px' }}>
                  {forgotSent ? "Check your email for a reset link." : "We'll email you a link to set a new one."}
                </p>

                {!forgotSent && (
                  <>
                    <label>Email</label>
                    <div className="login-input-wrap">
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                      />
                    </div>

                    {forgotError && (
                      <p style={{ fontSize: '12.5px', color: theme.danger, marginTop: '8px', marginBottom: 0, fontWeight: 600 }}>{forgotError}</p>
                    )}

                    <button
                      className="btn-primary login-submit-btn"
                      style={{ marginTop: '20px', width: '100%' }}
                      onClick={handleForgotPassword}
                      disabled={forgotSubmitting}
                    >
                      {forgotSubmitting ? 'Sending…' : 'Send reset link'}
                    </button>
                  </>
                )}

                <p style={{ textAlign: 'center', marginTop: '16px', color: theme.textMuted }}>
                  <span
                    onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotError(null); }}
                    style={{ color: theme.textPrimary, cursor: 'pointer', fontWeight: 700 }}
                  >
                    ← Back to sign in
                  </span>
                </p>
              </>
            ) : (
              <>
                <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
                  {isSignup ? 'Create your account' : 'Welcome back'}
                </h2>
                <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 24px' }}>
                  {isSignup ? 'Track your trips and get an email when a fare fits. You don’t need a card.' : 'Sign in to see your trips'}
                </p>

                {/* Referral acknowledgment (Aug 22, per Ali's report) — only
                    shown when a ?ref= code was actually detected on landing.
                    States the real reward (matches growth.py's apply_referral
                    exactly: both sides get a bonus active-pin slot) rather
                    than a vague "you were invited" — a specific, true benefit
                    is a stronger reason to finish signing up than a generic
                    welcome message, and it's the one thing the old version of
                    this page never told the person at all despite silently
                    capturing their referral code in the background. */}
                {referralCode && isSignup && (
                  <div style={{ background: theme.bg, border: `2px solid ${theme.action}`, padding: '12px 14px', marginBottom: '20px', fontSize: '12.5px', color: theme.textPrimary, lineHeight: 1.5 }}>
                    You've been invited. Sign up and you both get a bonus active-pin slot.
                  </div>
                )}

                {/* First name (Sep 8, per Ali's report) — signup only,
                    optional, purely for the Dashboard's "Welcome back,
                    {name}" greeting. No validation beyond the backend's
                    own non-empty/length check on submit — skipping this
                    is fine, the Dashboard falls back to a plain
                    "Welcome back" for anyone who does. */}
                {isSignup && (
                  <>
                    <label>First name</label>
                    <div className="login-input-wrap">
                      <input
                        name="first_name"
                        type="text"
                        placeholder="Sarah"
                        value={form.first_name}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                )}

                <label>Email</label>
                <div className="login-input-wrap">
                  <input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>

                <label>Password</label>
                <div className="login-input-wrap">
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={handleChange}
                  />
                  <PasswordVisibilityToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} />
                </div>

                {/* Live strength checklist (Sep 16, per Ali's report —
                    "at all levels," this same component is also used
                    on the Change/Reset Password screen). Signup only
                    — this is the one moment someone's actually
                    choosing a new password on this screen; signing
                    into an existing account has nothing to validate
                    here. */}
                {isSignup && <PasswordStrengthMeter password={form.password} />}

                {/* Only shown signing in, not creating an account — a
                    brand-new account has no existing password to have
                    forgotten yet. */}
                {!isSignup && (
                  <p style={{ textAlign: 'right', margin: '8px 0 0' }}>
                    <span
                      onClick={() => setShowForgotPassword(true)}
                      style={{ fontSize: '12.5px', color: theme.textMuted, cursor: 'pointer' }}
                    >
                      Forgot password?
                    </span>
                  </p>
                )}

                <button className="btn-primary login-submit-btn" style={{ marginTop: '20px', width: '100%' }} onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Please wait...' : isSignup ? 'Create account' : 'Sign in'}
                </button>

                {isSignup && (
                  <p style={{ textAlign: 'center', fontSize: '12.5px', color: theme.textMuted, margin: '12px 0 0', lineHeight: 1.5 }}>
                    By creating an account, you agree to our{' '}
                    <a href="https://flyatlast.com/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.textPrimary, textDecoration: 'underline' }}>Terms of Service</a>
                    {' '}and{' '}
                    <a href="https://flyatlast.com/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.textPrimary, textDecoration: 'underline' }}>Privacy Policy</a>.
                  </p>
                )}

                <p style={{ textAlign: 'center', marginTop: '16px', color: theme.textMuted }}>
                  {isSignup ? 'Already have an account?' : "Don't have an account?"}
                  <span
                    onClick={() => setIsSignup(!isSignup)}
                    style={{ color: theme.textPrimary, cursor: 'pointer', marginLeft: '8px', fontWeight: 700 }}
                  >
                    {isSignup ? 'Sign in' : 'Sign up'}
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    <AppFooter flush />
    </div>
  );
}

export default Login;